import React from 'react'
import { useAtomValue } from 'jotai'
import dt from 'dayjs'

import { ACCOUNT_PAGE_SORT_FIELDS, IAccountSortField, useMultiSelectTable } from '../../hooks'
import * as state from '../../states'
import {
	IAccountAuthLocal,
	IAccount,
	IRole,
	Part,
	EAccountIsActive,
	EAccountIsFired,
	ERoleAccess,
	ERoleExtraIsBuyer,
	ACCOUNT_IS_ACTIVE_LABEL_MAP,
	ACCOUNT_IS_FIRED_LABEL_MAP
} from '../../types'
import {
	UiTable,
	IUiTableConfig,
	IconDeleteBin,
	IconEdit,
	headCellFormatterSortable,
	ESortOrder,
	UiDateInfo,
	RoleExtraIsBuyerIcon,
	IconCopy
} from '../ui'
import { IS_ACTIVE_COLORS_MAP, ACCOUNT_IS_FIRED_COLORS_MAP } from '../../constants'
import { Access } from '../../lib'
import { useTranslation, TFunction } from 'react-i18next'
import { ALL_LANG_KEY } from '../../types/lang'


type IAccountListTableProps = {
	items: IAccount[];
	skip: number;
	sortBy: IAccountSortField;
	setSortBy: (sortBy: IAccountSortField) => void;
	order: ESortOrder;
	setOrder: (order: ESortOrder) => void;
	onEdit?: (item: IAccount, e: React.MouseEvent) => void;
	onClone?: (item: IAccount, e: React.MouseEvent) => void;
	onRemove?: (item: IAccount, e: React.MouseEvent) => void;
	onMultiSelect?: (selectedItems: IAccount[]) => void;
}

type IAccountIsActiveProps = {
	value: EAccountIsActive;
	rowData: IAccount; // { }
	onClick?: (rowData: IAccount, e: React.MouseEvent) => void;
	classNamesToAdd?: string;
}

export const AccountIsActiveField = (props: IAccountIsActiveProps) => {
	const { t } = useTranslation()
	const { value, classNamesToAdd, rowData, onClick } = props

	const keyTrans = ACCOUNT_IS_ACTIVE_LABEL_MAP?.[value]
	const labelTrans = t(keyTrans)

	return <>
		<span
			/*style={{cursor: 'pointer'}}*/
			className={`badge text-white fw-normal py-1 px-2 fs-12 rounded-1 edit ${classNamesToAdd ?? ''}`}
			onClick={(e) => onClick && onClick(rowData, e)}
		>
			{labelTrans}
		</span>
	</>
}

type IAccountIsFiredProps = {
	value: EAccountIsFired;
	rowData: IAccount; // { }
	onClick?: (rowData: IAccount, e: React.MouseEvent) => void;
	classNamesToAdd?: string;
}
//TODO: roll-up AccountIsActiveField + AccountIsFiredField components ...?
export const AccountIsFiredField = (props: IAccountIsFiredProps) => {
	const { t } = useTranslation()
	const { value, classNamesToAdd, rowData, onClick } = props

	const keyTrans = ACCOUNT_IS_FIRED_LABEL_MAP?.[value]
	const labelTrans = t(keyTrans)

	return <>
		<span
			/*style={{cursor: 'pointer'}}*/
			className={`badge text-white fw-normal py-1 px-2 fs-12 rounded-1 edit ${classNamesToAdd ?? ''}`}
			onClick={(e) => onClick && onClick(rowData, e)}
		>
			{labelTrans}
		</span>
	</>
}

export const getAccountIsFiredBadge = (
	value: boolean,
	t: TFunction,
	onlyLabel?: boolean,
	noStyles?: boolean
) => {
	const valueFormat: EAccountIsFired = value ? EAccountIsFired.Fired : EAccountIsFired.NotFired
	const classNamesToAdd = ACCOUNT_IS_FIRED_COLORS_MAP?.[valueFormat]

	const keyTrans = ACCOUNT_IS_FIRED_LABEL_MAP?.[valueFormat]
	const labelTrans = t(keyTrans)

	if (onlyLabel) {
		return labelTrans
	}

	return <>
		<span
			className={`badge text-white fw-normal py-1 px-2 fs-12 rounded-1 edit ${classNamesToAdd ?? ''}`}
			{...(
				!noStyles ? {
					style: {
						marginLeft: '7px'
						// transform: 'translateY(-50%)'
					}
				} : {}
			)}
		>
			{labelTrans}
		</span>
	</>
}

export const AccountListTable = (props: IAccountListTableProps) => {
	const {
		items = [],
		skip = 0,
		sortBy,
		setSortBy,
		order,
		setOrder,
		onEdit,
		onClone,
		onRemove,
		onMultiSelect
	} = props
	const { t } = useTranslation()
	const profile = useAtomValue(state.profileAtom)
	const $access = new Access(profile ?? null)
	const isMultiSelectEnabled = !!$access.$(ERoleAccess.AccountRemove)

	const { multiSelectConfig, selectedRowIndexes } = useMultiSelectTable(
		{
			items,
			isEnabled: isMultiSelectEnabled,
			onMultiSelect
		}
	)

	const accountListTableConfig: IUiTableConfig<IAccount>[] = [
		{
			key: 'id',
			title: t(ALL_LANG_KEY.account_list_table.column_id)
		},
		{
			key: 'email',
			title: t(ALL_LANG_KEY.account_list_table.column_email)
		},
		{
			key: 'auth.login',
			title: t(ALL_LANG_KEY.account_list_table.column_login),
			rowCellFormatter: ({ rowData }) => {
				const auth: Part<IAccountAuthLocal, 'updatedAt'> | null = rowData.auth ?? null
				const valueFormat: string = auth?.login ?? ''
				return valueFormat
			}
		},
		{
			key: 'extra.keitaroLogin',
			title: t(ALL_LANG_KEY.account_list_table.column_kt_group),
			rowCellFormatter: ({ rowData }) => {
				const extra = rowData.extra ?? null
				const valueFormat: string = extra?.keitaroLogin ?? ''
				return valueFormat
			}
		},
		{
			key: 'role.name',
			title: t(ALL_LANG_KEY.account_list_table.column_role),
			rowCellFormatter: ({ value, rowData, rowsData, rowIndex, cellIndex }) => {
				const role: Part<IRole, 'createdAt' | 'updatedAt'> | null = rowData.role ?? null
				const valueFormat: string = role ? `${role.name} (${role.code})` : '?'

				return valueFormat
			}
		},
		{
			key: 'role.extra.isBuyer',
			title: t(ALL_LANG_KEY.account_list_table.column_is_buyer),
			rowCellFormatter: ({ rowData }) => {
				const isBuyer = rowData.role?.extra.isBuyer
				return <div style={{ textAlign: 'center' }}>
					<RoleExtraIsBuyerIcon
						value={isBuyer ? ERoleExtraIsBuyer.Buyer : isBuyer === false ? ERoleExtraIsBuyer.NotBuyer : null}/>
				</div>
			}
		},
		{
			key: 'role.extra.inGroup',
			title: t(ALL_LANG_KEY.account_list_table.column_in_group),
			rowCellFormatter: ({ rowData }) => {
				const inGroup = rowData.role?.extra.inGroup ?? null
				return <div style={{ textAlign: 'center' }}>
					{inGroup}
				</div>
			}
		},
		{
			key: 'role.extra.isTeam',
			title: t(ALL_LANG_KEY.account_list_table.column_is_team),
			rowCellFormatter: ({ rowData }) => {
				const isTeam = rowData.role?.extra.isTeam ?? null
				return <div style={{ textAlign: 'center' }}>
					{isTeam}
				</div>
			}
		},
		{
			key: 'isActive',
			title: t(ALL_LANG_KEY.account_list_table.column_is_active),
			headCellFormatter: ({ value, configItem }) => {
				return <div style={{ textAlign: 'center' }}>{value}</div>
			},
			rowCellFormatter: ({ value, rowData, rowIndex, cellIndex }) => {
				const valueFormat: EAccountIsActive = value ? EAccountIsActive.active : EAccountIsActive.unactive

				return <div style={{ textAlign: 'center' }}>
					<AccountIsActiveField
						value={valueFormat}
						rowData={rowData}
						classNamesToAdd={IS_ACTIVE_COLORS_MAP?.[valueFormat]}
						onClick={(rowData, e) => {
							console.log('Click on Status field :::', { value, rowData, rowIndex, cellIndex })
						}}
					/>
				</div>
			}
		},
		{
			key: 'isFired',
			title: t(ALL_LANG_KEY.account_list_table.column_is_fired),
			classesToAdd: {
				th: 'text-center',
				td: 'text-center'
			},
			rowCellFormatter: ({ value, rowData, rowIndex, cellIndex }) => {
				const valueFormat: EAccountIsFired = value ? EAccountIsFired.Fired : EAccountIsFired.NotFired

				return <>
					<AccountIsFiredField
						value={valueFormat}
						rowData={rowData}
						classNamesToAdd={ACCOUNT_IS_FIRED_COLORS_MAP?.[valueFormat]}
						onClick={(rowData, e) => {
							console.log('Click on Fired field :::', { value, rowData, rowIndex, cellIndex })
						}}
					/>
				</>
			}
		},
		{
			key: 'registeredAt',
			title: t(ALL_LANG_KEY.account_list_table.column_date_registered),
			headCellFormatter: ({ value, configItem, cellIndex }) => {
				return <div style={{ textAlign: 'center' }}>{value}</div>
			},
			rowCellFormatter: ({ value, rowData, rowIndex, cellIndex }) => {
				return (
					<UiDateInfo value={value} styles={{ textAlign: 'center' }}/>
				)
			}
		},
		{
			key: 'ACTIONS',
			title: 'Actions',
			headCellFormatter: ({ configItem }) => {
				return <div>{''}</div>
			},
			rowCellFormatter: ({ rowData }) => {
				return <>
					<div
						style={{
							margin: '0 auto',
							textAlign: 'center'
						}}
					>

						{
							!!onEdit && !!$access.$(ERoleAccess.AccountModify)
								? (
									<button name="edit"
									        className="icon border-0 rounded-circle text-center edit bg-success-transparent edit-item"
									        onClick={(e) => onEdit && onEdit(rowData, e)}
									>
										<IconEdit/>
									</button>
								)
								: null
						}

						{
							!!onClone && !!$access.$(ERoleAccess.AccountModify)
								? (
									<button name="clone"
									        className="icon border-0 rounded-circle text-center bg-primary-transparent"
									        onClick={(e) => onClone && onClone(rowData, e)}
									>
										<IconCopy/>
									</button>
								)
								: null
						}

						{
							!!onRemove && !!$access.$(ERoleAccess.AccountRemove)
								? (
									<button
										name="delete"
										className="icon border-0 rounded-circle text-center trash bg-danger-transparent delete-item"
										onClick={(e) => onRemove && onRemove(rowData, e)}
									>
										<IconDeleteBin/>
									</button>
								)
								: null
						}
					</div>
				</>
			}
		}
	]

	const accountListTableConfigSortable: IUiTableConfig<IAccount>[] = accountListTableConfig.map(
		(configItem) => {
			const configItemIsSortable = ACCOUNT_PAGE_SORT_FIELDS.includes(configItem.key as IAccountSortField)

			if (!configItemIsSortable) {
				return configItem
			} else {
				return {
					...configItem,
					isSortable: true,
					headCellFormatter: ({
						value,
						configItem,
						cellIndex
					}) => {

						return headCellFormatterSortable({
							value,
							configItem,
							cellIndex,
							sortBy,
							setSortBy,
							order,
							setOrder
						})
					}
				}
			}
		})

	//console.log('AccountListTable:: ', { accountListTableConfig, accountListTableConfigSortable })
	const tableConfig = isMultiSelectEnabled ? [multiSelectConfig, ...accountListTableConfigSortable] : accountListTableConfigSortable

	return <>
		<UiTable
			data={items}
			config={tableConfig}
			selectedRowIndexes={selectedRowIndexes}
			// TODO: uncomment if necessary
			// classesToAdd={{
			// 	wrapper: 'table-sticky-wrapper',
			// }}
		/>
	</>
}